import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    SEARCH_LOCAL_STORAGE_ID, SEARCH_ACTION, SEARCH_OBJECT_TYPE, GA4_EVENT, GA4_METHOD,
} from '@common/GlobalSearch/constants';
import { useRecentSearch } from '@common/GlobalSearch/hooks/useRecentSearch';
import { useConfig } from '@common/Hooks/useConfig';
import { isRecentsSessionExpired } from '@common/Recents/actions/recentsActions';
import { GA_SEARCH_CATEGORY, REAL_LIST_VALUES } from '@common/Tracking';
import {
    GA_SEARCH_VIA_AUTOCOMPLETE_ACTION,
    GA_SEARCH_VIA_RECENT_SEARCHES_ACTION,
    GA_SEARCH_SPELLCHECKED_SUGGESTED_CLICKED_ACTION,
    GA_SEARCH_SPELLCHECKED_SUGGESTED_VISIBLE_ACTION,
    GA_SEARCH_TAB_SUGGESTIONS_VISIBLE_ACTION,
} from '@common/Tracking/constants/GaEventActions';
import { SEARCH_SUGGESTION_PRODUCT } from '@common/Tracking/constants/localStorage';
import { storeFormattedTrackingParams } from '@common/Tracking/searchResultPage/storeFormattedTrackingParams';
import { useTranslate } from '@common/Translations';
import { ga4event, pushGAEventToDataLayer } from '@helpers/dataLayer';
import { isWebshopDesktop } from '@helpers/env';
import { isFeatureActive } from '@helpers/index';
import { saveState } from '@helpers/localStorage';
import { useLocalStorageObject } from '@helpers/localStorage/hooks';
import { GlobalSearchNoResultPage } from '../components/GlobalSearchNoResultPage';
import GlobalSearchResults from '../components/GlobalSearchResults';
import { getSelectedSearchResult, getSelectedRecentSearch, convertToLowerCase } from '../helpers';
import type { GlobalSearchResultsData } from '../types';
import '../styles/GlobalSearchResults.scss';

interface GlobalSearchResultsContainerProps {
    searchResults: GlobalSearchResultsData;
    searchTerm: string;
    onTouchScreen: (state: boolean) => void;
}

function GlobalSearchResultsContainer({ searchResults, searchTerm, onTouchScreen }: GlobalSearchResultsContainerProps) {
    const config = useConfig();
    const {
        addRecentSearch,
        getRecentSearchesList,
        deleteAllRecentSearches,
        recentSearches,
    } = useRecentSearch({ limit: 10 });
    const { clear: clearRecentSearches } = useLocalStorageObject(SEARCH_LOCAL_STORAGE_ID.newRecentSearches);
    const activatedDeals = useSelector((state: any) => state.activateDeals.activated);
    const translate = useTranslate();
    const [hasTrackedSuggestionsEvent, setHasTrackedSuggestionsEvent] = useState(false);
    const [hasTrackedSpellcheckedSuggestionsEvent, setHasTrackedSpellcheckedSuggestionsEvent] = useState(false);
    const isDesktopView = isWebshopDesktop(config);
    const hasSearchTerm = searchTerm?.trim()?.length > 0;
    const hasRecentSearch = !!recentSearches?.length;
    const isEmptyResult = !!searchResults?.metadata?.empty_result;
    const isNoResultPageVisible = hasSearchTerm ? isEmptyResult : !hasRecentSearch;
    const isRecentSearchesVisible = hasRecentSearch && !hasSearchTerm;
    const { spell_checked: spellChecked = false } = searchResults?.metadata ?? {};
    const suggestedQuery = searchResults?.metadata?.suggested_query;
    const isMediaDiscountVoucher = activatedDeals.length > 0;
    const totalProductsCount = searchResults?.products?.items.length;

    const handleSuggestionClick = (navigationId: number) => {
        const selectedSearchResult = getSelectedSearchResult(searchResults, navigationId);

        saveState(SEARCH_LOCAL_STORAGE_ID.searchResults, {
            searchKey: selectedSearchResult?.title,
            action: SEARCH_ACTION.autoComplete,
            objectType: selectedSearchResult?.type,
            navigationId: selectedSearchResult?.navigation_id,
            path: selectedSearchResult?.path,
        });

        pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_VIA_AUTOCOMPLETE_ACTION, selectedSearchResult?.title);

        ga4event(GA4_EVENT.search, {
            search_term: convertToLowerCase(searchTerm),
            search_method: GA4_METHOD.viaSearchSuggestions,
        });

        if (spellChecked) {
            pushGAEventToDataLayer(
                GA_SEARCH_CATEGORY,
                GA_SEARCH_SPELLCHECKED_SUGGESTED_CLICKED_ACTION,
                selectedSearchResult?.title,
                null,
                {},
                { typed_query: searchTerm },
            );
        }
    };

    const handleRecentSearchClick = (id: string) => {
        const selectedRecentSearch = getSelectedRecentSearch(recentSearches, id);
        const selectedActionType = (selectedRecentSearch?.type === SEARCH_ACTION.hardSearch)
            ? SEARCH_ACTION.search
            : SEARCH_ACTION.autoComplete;

        saveState(SEARCH_LOCAL_STORAGE_ID.searchResults, {
            searchKey: selectedRecentSearch?.title,
            action: selectedActionType,
            objectType: selectedRecentSearch?.object_type,
            navigationId: selectedRecentSearch?.navigation_id,
            path: selectedRecentSearch?.path,
        });

        pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_VIA_RECENT_SEARCHES_ACTION, selectedRecentSearch?.name);

        ga4event(GA4_EVENT.search, {
            search_term: convertToLowerCase(selectedRecentSearch?.title),
            search_method: GA4_METHOD.viaRecentSearches,
        });
    };

    const handleDeleteAllClick = () => {
        clearRecentSearches();

        if (isFeatureActive('PLT-9525-show-recent-searches')) {
            deleteAllRecentSearches();
        }
    };

    const handleProductClick = (eanId: number, index: number, productId: number, variantId: number) => {
        addRecentSearch({
            searchKey: searchTerm,
            action: SEARCH_ACTION.autoComplete,
            objectType: SEARCH_OBJECT_TYPE.product,
            navigationId: null,
            path: null,
            eanId,
            productId,
        });

        storeFormattedTrackingParams(SEARCH_SUGGESTION_PRODUCT, {
            currentPage: 1,
            productId,
            productIndex: index,
            productsGroup: REAL_LIST_VALUES.search,
            productsPerRow: 1,
            productsPerPage: totalProductsCount,
            totalProducts: totalProductsCount,
            variantId,
        });
    };

    useEffect(() => {
        const isExpired = isRecentsSessionExpired();

        if (isExpired) {
            getRecentSearchesList();
        }
    }, [getRecentSearchesList]);

    useEffect(() => {
        if (hasSearchTerm) {
            if (!hasTrackedSuggestionsEvent) {
                pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_TAB_SUGGESTIONS_VISIBLE_ACTION, '');
                setHasTrackedSuggestionsEvent(true);
            }

            if (!hasTrackedSpellcheckedSuggestionsEvent && spellChecked) {
                pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_SPELLCHECKED_SUGGESTED_VISIBLE_ACTION, '');
                setHasTrackedSpellcheckedSuggestionsEvent(true);
            }
        }
    }, [hasSearchTerm, hasTrackedSuggestionsEvent,
        spellChecked, hasTrackedSpellcheckedSuggestionsEvent]);

    if (isNoResultPageVisible) {
        return (
            <GlobalSearchNoResultPage
                isEmptyResult={isEmptyResult}
                subtitle={translate('global_search.no_result_subheader')}
                title={isEmptyResult
                    ? translate('global_search.no_result_header', { search: searchTerm })
                    : translate('search_empty_state_label')}
            />
        );
    }

    return (
        <GlobalSearchResults
            headerButtonLabel={translate('global-search.clear-button')}
            headerTitle={translate('global-search.section-header.recent-searches')}
            isDesktopView={isDesktopView}
            isMediaDiscountVoucher={isMediaDiscountVoucher}
            isRecentSearchesVisible={isRecentSearchesVisible}
            recentSearches={recentSearches}
            searchResults={searchResults}
            searchTerm={searchTerm}
            suggestedQuery={suggestedQuery}
            suggestedQueryLabel={translate('search_autocorrection_title', { search: searchTerm, suggestion: suggestedQuery })}
            onDeleteAllClick={handleDeleteAllClick}
            onProductClick={handleProductClick}
            onRecentSearchClick={handleRecentSearchClick}
            onSuggestionClick={handleSuggestionClick}
            onTouchScreen={onTouchScreen}
        />
    );
}

export default GlobalSearchResultsContainer;
